import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Space, Spin } from 'antd';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useAggregationConfig } from '../../../../hooks/SimViewer/useAggregationConfig';
import { useLoadingStatus } from '../../../../hooks/SimViewer/useLoadingStatus';
import { useDebounce } from '../../../../hooks/useDebounce';
import {
  useLegendConfig,
  MIN_SCALE_SIZE,
  MAX_SCALE_SIZE,
} from '../../../../hooks/SimViewer/useLegendConfig';
import { useMapColorPalette } from '../../../../hooks/SimViewer/useMapColorPalette';
import { useDataFilter } from '../../../../hooks/SimViewer/useDataFilter';
import { COLORS } from '../../../../constants/color';
import { formatAny } from '../../../../utils/format';
import { parseAny } from '../../../../utils/parse';
import type { Legend } from '../../../../types/common';
import {
  alpha,
  toHex,
  type HexColor,
  type RgbColor,
} from '../../../../utils/color';

type Props = {
  legends: Legend[];
};

const LegendList = ({ legends }: Props): ReactNode => {
  const { dataFilter } = useDataFilter();
  const { t } = useTranslation();
  const { aggregationConfig } = useAggregationConfig();
  const { isLoadingAggregation } = useLoadingStatus();
  const {
    scaleType,
    isEditingLegend,
    draftCustomRange,
    isValidDraftCustomRange,
    updateDraftCustomRange,
    addDraftCustomRange,
    removeDraftCustomRange,
  } = useLegendConfig();
  const { getRgbColors, mapColorOpacity } = useMapColorPalette();
  const { debounce: debounce1 } = useDebounce(1000);
  const { debounce: debounce2 } = useDebounce(1000);

  const [localCustomRange, setLocalCustomRange] = useState<[string, string][]>(
    []
  );

  const isEditing = scaleType === 'custom' && isEditingLegend;

  const valueType = useMemo(():
    'duration' | 'distance' | 'count' | 'countPerArea' => {
    switch (aggregationConfig?.metric) {
      case 'average_traveled_time':
      case 'average_activity_time':
        return 'duration';
      case 'average_traveled_distance':
        return 'distance';
      case 'count_per_area':
        switch (aggregationConfig.type) {
          case 'areas':
          case 'area_pref':
            return 'countPerArea';
          default:
            return 'count';
        }
      default:
        return 'count';
    }
  }, [aggregationConfig]);

  const unit = useMemo((): string => {
    if (
      dataFilter.longestDistanceModes.length === 1 &&
      dataFilter.longestDistanceModes[0] === 'car'
    ) {
      return t('unit.car');
    }
    return t('unit.agent');
  }, [dataFilter.longestDistanceModes, t]);

  // 編集中は凡例の数が確定前の状態に追従するため、色も編集中の数に合わせて求める
  const draftColors = useMemo((): HexColor[] => {
    return getRgbColors(draftCustomRange.length).map(
      (color: RgbColor): HexColor => {
        return toHex(alpha(color, mapColorOpacity));
      }
    );
  }, [draftCustomRange.length, getRgbColors, mapColorOpacity]);

  // 編集中の値が外部から変更されたときにローカルの状態も更新する
  useEffect((): void => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLocalCustomRange(
      draftCustomRange.map((range: [number, number]): [string, string] => {
        return [
          !Number.isNaN(range[0]) ? formatAny(valueType, range[0], unit) : '',
          !Number.isNaN(range[1]) ? formatAny(valueType, range[1], unit) : '',
        ];
      })
    );
  }, [draftCustomRange, valueType, unit]);

  return (
    <Spin spinning={isLoadingAggregation}>
      <LegendContainer>
        {isEditing ? (
          <>
            {draftCustomRange.map(
              (range: [number, number], index: number): ReactNode => {
                return (
                  <LegendItem key={`${index}/${draftCustomRange.length}`}>
                    <MinusButton
                      type="button"
                      name={`legend-remove-${index}`}
                      disabled={draftCustomRange.length <= MIN_SCALE_SIZE}
                      onClick={(): void => {
                        removeDraftCustomRange(index);
                      }}
                    >
                      <MinusOutlined />
                    </MinusButton>
                    <ColorBar style={{ backgroundColor: draftColors[index] }} />
                    <Space size={4}>
                      <NumberInput
                        type="text"
                        inputMode="numeric"
                        name={`legend-lower-${index}`}
                        value={localCustomRange[index]?.[0] ?? ''}
                        data-error={
                          !!localCustomRange[index]?.[0] &&
                          !isValidDraftCustomRange(index, [
                            parseAny(valueType, localCustomRange[index][0]) ??
                              Number.NaN,
                            range[1],
                          ])
                        }
                        onChange={(
                          event: ChangeEvent<HTMLInputElement>
                        ): void => {
                          const value = event.target.value.trim();
                          const parsedValue = parseAny(valueType, value);

                          setLocalCustomRange([
                            ...localCustomRange.slice(0, index),
                            [value, localCustomRange[index]?.[1] ?? ''],
                            ...localCustomRange.slice(index + 1),
                          ]);

                          debounce1((): void => {
                            updateDraftCustomRange(
                              index,
                              'lower',
                              parsedValue ?? Number.NaN
                            );
                          });
                        }}
                      />
                      ~
                      <NumberInput
                        type="text"
                        inputMode="numeric"
                        name={`legend-upper-${index}`}
                        value={localCustomRange[index]?.[1] ?? ''}
                        data-error={
                          !!localCustomRange[index]?.[1] &&
                          !isValidDraftCustomRange(index, [
                            range[0],
                            parseAny(valueType, localCustomRange[index][1]) ??
                              Number.NaN,
                          ])
                        }
                        onChange={(
                          event: ChangeEvent<HTMLInputElement>
                        ): void => {
                          const value = event.target.value.trim();
                          const parsedValue = parseAny(valueType, value);

                          setLocalCustomRange([
                            ...localCustomRange.slice(0, index),
                            [localCustomRange[index]?.[0] ?? '', value],
                            ...localCustomRange.slice(index + 1),
                          ]);

                          debounce2((): void => {
                            updateDraftCustomRange(
                              index,
                              'upper',
                              parsedValue ?? Number.NaN
                            );
                          });
                        }}
                      />
                    </Space>
                  </LegendItem>
                );
              }
            )}
            <PlusButton
              type="button"
              name="legend-add"
              disabled={MAX_SCALE_SIZE <= draftCustomRange.length}
              onClick={(): void => {
                addDraftCustomRange();
              }}
            >
              <PlusOutlined />
            </PlusButton>
          </>
        ) : (
          legends.map((legend: Legend, index: number): ReactNode => {
            return (
              <LegendItem key={index}>
                <ColorBar style={{ backgroundColor: toHex(legend.color) }} />
                <span>
                  {legend.label ??
                    `${formatAny(valueType, legend.range[0], unit)} ~ ${formatAny(valueType, legend.range[1], unit)}`}
                </span>
              </LegendItem>
            );
          })
        )}
      </LegendContainer>
    </Spin>
  );
};

const LegendContainer = styled.div`
  width: fit-content;
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: start;
  gap: 4px;
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
  gap: 8px;
`;

const ColorBar = styled.div`
  width: 28px;
  height: 12px;
`;

const NumberInput = styled.input`
  width: 80px;
  height: 22px;
  padding: 0;
  text-align: center;
  border: none;
  border: 1px solid ${alpha(COLORS.lightGray, 0.4)};
  border-radius: 2px;
  &[data-error='true'] {
    border: 1px solid ${COLORS.red};
  }
  &:focus {
    outline: none;
  }
`;

const MinusButton = styled.button`
  width: 18px;
  height: 18px;
  padding: 0;
  margin-right: -4px;
  background-color: ${COLORS.transparent};
  color: ${alpha(COLORS.gray, 0.9)};
  border: none;
  border-radius: 50%;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  &:hover:not(:disabled) {
    background-color: ${alpha(COLORS.lightGray, 0.2)};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PlusButton = styled.button`
  width: calc(100% - 22px);
  height: 22px;
  box-sizing: border-box;
  padding: 0;
  margin-left: 22px;
  font-size: 14px;
  line-height: 1;
  background-color: ${COLORS.transparent};
  color: ${alpha(COLORS.lightGray, 0.8)};
  border: 1px dashed ${alpha(COLORS.lightGray, 0.8)};
  border-radius: 2px;
  cursor: pointer;
  &:hover:not(:disabled) {
    color: ${alpha(COLORS.gray, 0.9)};
    border: 1px dashed ${alpha(COLORS.gray, 0.9)};
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export { LegendList };
